import { MetricConfig, SeverityType, AutoInsightOption, AutoInsightConfig } from './types';
import { AUTO_INSIGHT_NOTIFICATION_ITEMS_MAP } from '@src/components/notification-channel/utils';

export const getESMetricSeverityMap = (metricSeverityObject) => {
  const aiAlertMetrics: MetricConfig[] = [];
  const autoInsightItems: AutoInsightConfig[] = [];
  Object.keys(metricSeverityObject).forEach((key) => {
    const curItem = metricSeverityObject[key];
    if (!curItem) {
      return;
    }
    const isAutoInsightItem = !!AUTO_INSIGHT_NOTIFICATION_ITEMS_MAP.find(
      (autoInsightItem) => autoInsightItem.name === key,
    );
    if (!isAutoInsightItem) {
      // ai alert
      curItem.severity &&
        aiAlertMetrics.push({
          metric_key: key,
          severity: curItem.severity,
        });
    } else {
      // auto insight
      curItem.option &&
        autoInsightItems.push({
          type: key,
          severity: 'info',
          option: curItem.option as AutoInsightOption,
        });
    }
  });

  return { aiAlertMetrics, autoInsightItems };
};

export const getESDefaultMetricCategories = () => ({
  EI: { STANDARD_METRICS: 'Metrics' },
  APP: { STANDARD_METRICS: 'Standard Metrics', CUSTOM_METRICS: 'Custom Metrics' },
  AI: { STANDARD_METRICS: 'Metrics' },
});

export const isSearchedItem = (itemName: string, searchText: string) => {
  const regex = new RegExp(searchText, 'i'); // ignore case
  return regex.test(itemName);
};

// Canonicalize emails (trim + lowercase) before they are sent to the server so
// the same address entered with different casing can't create duplicate
// subscription entries. See CE-12191.
export const normalizeEmail = (email: string): string =>
  typeof email === 'string' ? email.trim().toLowerCase() : email;

export const normalizeEmails = (emails: string[] = []): string[] =>
  Array.from(new Set(emails.map(normalizeEmail)));

export const validateEmailArray = (emailArray) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return emailArray.length > 0
    ? emailArray.every((email) => re.test(email))
      ? Promise.resolve()
      : Promise.reject(new Error('Please enter valid emails.'))
    : Promise.reject(new Error('At least one email is required'));
};

export const getFlattenedMetricSeverity = ({
  allESMetricKeys,
  subscriptionMetrics,
  subscriptionMetricsAutoInsights,
  type,
}) => {
  const flattenedESMetrics: {
    [k: string]: {
      severity: SeverityType;
      option?: AutoInsightOption;
    };
  } = {};
  // AI Alert
  if (subscriptionMetrics?.length) {
    subscriptionMetrics.forEach(
      (m) =>
        (flattenedESMetrics[m.metric_key] = {
          severity: m.severity,
        }),
    );
  } else if (type === 'create') {
    // for ai alert, we only give default values when we create a new subscription
    allESMetricKeys.forEach(
      (m) =>
        (flattenedESMetrics[m] = {
          severity: 'info',
        }),
    );
  }

  // handle auto insight
  if (subscriptionMetricsAutoInsights?.length) {
    // edit an existing auto insight item
    subscriptionMetricsAutoInsights.forEach(
      (m) =>
        (flattenedESMetrics[m.type] = {
          severity: 'info',
          option: m.option,
        }),
    );
  } else {
    AUTO_INSIGHT_NOTIFICATION_ITEMS_MAP.forEach((item) => {
      flattenedESMetrics[item.name] = {
        severity: 'info',
        // if we create a new subscription the default value is all
        // if we editing a existing subscription the default value is null
        option: type === 'create' ? 'all' : '',
      };
    });
  }

  return flattenedESMetrics;
};
